import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { HERO_VIDEO_CAROUSEL_SLIDES } from '../constants/heroVideoCarousel';
import { YouTubeEmbed } from './YouTubeEmbed';

const AUTO_MS = 11000;

export function ProductVideoCarousel({ className = '' }) {
  const slides = HERO_VIDEO_CAROUSEL_SLIDES;
  const n = slides.length;
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (dir) => {
      setIndex((i) => (i + dir + n) % n);
    },
    [n]
  );

  useEffect(() => {
    if (reduceMotion || paused || n <= 1) return undefined;
    const id = window.setInterval(() => go(1), AUTO_MS);
    return () => window.clearInterval(id);
  }, [go, n, paused, reduceMotion]);

  const slide = slides[index];

  return (
    <div
      className={`rounded-2xl bg-gradient-to-b from-[#121314] to-[#0a0a0b] border border-white/[0.1] shadow-[0_48px_120px_-48px_rgba(0,0,0,0.75)] ring-1 ring-white/[0.04] ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="p-3 sm:p-4 border-b border-white/[0.06]">
        <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-400/85 mb-3 px-0.5">
          Product films · rotate to compare
        </p>
        <div className="flex flex-wrap gap-2">
          {slides.map((s, i) => (
            <button
              key={s.videoId}
              type="button"
              onClick={() => setIndex(i)}
              className={`px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] transition-all duration-200 border ${
                i === index
                  ? 'bg-teal-500/15 border-teal-400/40 text-teal-200'
                  : 'bg-white/[0.03] border-white/[0.08] text-zinc-500 hover:text-zinc-300 hover:border-white/15'
              }`}
            >
              {s.shortLabel}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 sm:p-4 pt-2">
        <YouTubeEmbed key={slide.videoId} videoId={slide.videoId} title={slide.title} tone="cinema" loop />
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 px-0.5">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal-400/80 mb-1">{slide.modality}</p>
            <p className="text-sm text-zinc-200 leading-relaxed">{slide.solve}</p>
          </div>
          <div className="sm:border-l sm:border-white/[0.08] sm:pl-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Replaces</p>
            <p className="text-sm text-zinc-400 leading-relaxed">{slide.replaces}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 px-0.5">
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-teal-400/80' : 'w-1.5 bg-zinc-600'}`}
                aria-hidden
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => go(-1)}
              className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Previous film"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Next film"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
